import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import styles from './index.module.scss';
import Card from '@components-clean/core/card';
import EditCard from '@components-clean/core/edit-card';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

type SummaryFormValues = {
    summary: string;
};

type Props = {
    canEdit: boolean;
    isHalfScreen?: boolean;
    studyRef: string;
    reviewRecordId: number;
    currentSummary: string | null | undefined;
};

export const COMPONENT_NAME = 'active-review-summary-section';

const SummarySection = ({ canEdit, isHalfScreen, studyRef, reviewRecordId, currentSummary }: Props) => {
    const { t: tCommon } = useTranslation('common');
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.activeReviewTab.tabs.summary.summary' });

    const isEditMode = useBoolean(false);

    const updateReviewSummaryMutation = SubjectMonitoringReviewAPI.useUpdateReviewSummaryMutation(
        studyRef,
        reviewRecordId,
    );

    // reviewData is expected to change (refresh) after review is successfully updated
    const defaultValues = useMemo<SummaryFormValues>(() => {
        return {
            summary: currentSummary ?? '',
        };
    }, [currentSummary]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isValid, isSubmitting },
    } = useForm<SummaryFormValues>({
        defaultValues,
        mode: 'all',
    });

    useEffect(() => {
        reset(defaultValues, { keepDirty: false });
    }, [reset, defaultValues]);

    useUnsavedChangesTracking({
        area: 'subject-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'subject-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCancel = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        reset();
        isEditMode.setFalse();
    };

    const onSave = handleSubmit(async ({ summary }) => {
        let success;

        try {
            success = await updateReviewSummaryMutation.trigger({
                summary,
            });
        } catch {
            success = false;
        }

        if (success) {
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } else {
            ShowError(tCommon('errorMessage'));
        }

        isEditMode.setFalse();
    });

    const canSave = isDirty && isValid && !isSubmitting;

    if (!canEdit) {
        return (
            <Card
                data-testid="summary-card"
                data-is-half-screen={isHalfScreen}
                className={cn(styles.gridCell, styles.summarySection)}
                bodyClassName={cn(styles.gridCellInner, styles.summarySectionInner)}
                title={t('title')}
            >
                <TextAreaController
                    data-testid="summary-textarea"
                    className={styles.summaryTextarea}
                    control={control}
                    name="summary"
                    maxLength={3000}
                    readOnly={!isEditMode.value}
                />
            </Card>
        );
    }

    return (
        <EditCard
            data-testid="summary-card"
            data-is-half-screen={isHalfScreen ?? true}
            className={cn(styles.gridCell, styles.summarySection)}
            bodyClassName={cn(styles.gridCellInner, styles.summarySectionInner)}
            title={t('title')}
            isEditMode={isEditMode.value}
            onEdit={isEditMode.setTrue}
            onCancel={onCancel}
            onSave={onSave}
            saveDisabled={!canSave}
        >
            <TextAreaController
                data-testid="summary-textarea"
                className={styles.summaryTextarea}
                control={control}
                name="summary"
                maxLength={3000}
                readOnly={!isEditMode.value}
            />
        </EditCard>
    );
};

export default SummarySection;
